import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Loader2, Check, Plus, CreditCard, Calendar, Users, MapPin, Mail, Phone, AlertCircle, RefreshCw, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';
import SquarePaymentForm from '../components/SquarePaymentForm';

export default function ManageBookingPage() {
  const [bookingRef, setBookingRef] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [showAddAddonDialog, setShowAddAddonDialog] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [addonAdding, setAddonAdding] = useState(false);
  
  // Booking Management Token - stored in sessionStorage for security
  const [bookingToken, setBookingToken] = useState(null);
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null);
  
  // Payment Plan states
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [showPaymentPlanDialog, setShowPaymentPlanDialog] = useState(false);
  const [paymentPlanStep, setPaymentPlanStep] = useState(1); // 1: Info, 2: Card, 3: Confirm
  const [paymentPlanCalc, setPaymentPlanCalc] = useState(null); // Calculated payment plan details
  const [storedCards, setStoredCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [customerConsent, setCustomerConsent] = useState(false);
  
  // Update Card states
  const [showUpdateCardDialog, setShowUpdateCardDialog] = useState(false);
  const [updatingCard, setUpdatingCard] = useState(false);

  // Helper to get auth headers with booking token
  const getBookingAuthHeaders = () => {
    if (!bookingToken) return {};
    return {
      'X-Booking-Token': bookingToken
    };
  };

  // Check if token is expired
  const isTokenExpired = () => {
    if (!tokenExpiresAt) return true;
    return new Date(tokenExpiresAt) <= new Date();
  };

  // Handle token expiration gracefully
  const handleTokenExpired = () => {
    setBookingToken(null);
    setTokenExpiresAt(null);
    setBookingData(null);
    setPaymentPlan(null);
    sessionStorage.removeItem('booking_token');
    sessionStorage.removeItem('booking_token_expires');
    toast.error('Your booking session has expired. Please verify your booking again.');
  };

  // Restore token from sessionStorage on mount
  useEffect(() => {
    const storedToken = sessionStorage.getItem('booking_token');
    const storedExpires = sessionStorage.getItem('booking_token_expires');
    if (storedToken && storedExpires) {
      if (new Date(storedExpires) > new Date()) {
        setBookingToken(storedToken);
        setTokenExpiresAt(storedExpires);
      } else {
        // Token expired, clear it
        sessionStorage.removeItem('booking_token');
        sessionStorage.removeItem('booking_token_expires');
      }
    }
  }, []);

  const lookupBooking = async (e) => {
    e.preventDefault();
    if (!bookingRef.trim() || !email.trim()) {
      toast.error('Please enter both booking reference and email');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/bookings/lookup?booking_id=${encodeURIComponent(bookingRef.trim())}&email=${encodeURIComponent(email.trim())}`
      );
      
      // Safari-safe: Read as text first, then parse JSON
      const responseText = await response.text();
      let data;
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid response');
      }
      
      if (response.ok) {
        setBookingData(data);
        
        // Store the booking management token
        if (data.booking_token) {
          setBookingToken(data.booking_token);
          setTokenExpiresAt(data.token_expires_at);
          // Store in sessionStorage for persistence during page navigation
          sessionStorage.setItem('booking_token', data.booking_token);
          sessionStorage.setItem('booking_token_expires', data.token_expires_at);
        }
        
        toast.success('Booking found!');
        // Fetch payment plan if exists (with token)
        fetchPaymentPlan(data.booking.booking_id, data.booking_token);
        // Fetch stored cards (with token)
        fetchStoredCards(data.booking.customer_email, data.booking_token);
      } else {
        toast.error(data.detail || 'Booking not found');
        setBookingData(null);
        setPaymentPlan(null);
        setBookingToken(null);
        setTokenExpiresAt(null);
      }
    } catch (error) {
      console.error('Lookup error:', error);
      toast.error('Failed to look up booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentPlan = async (bookingId, token = bookingToken) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/weekends/bookings/${bookingId}/payment-plan`, {
        headers: {
          'X-Booking-Token': token
        }
      });
      
      if (response.status === 401) {
        handleTokenExpired();
        return;
      }
      
      const data = await response.json();
      setPaymentPlan(data.plan);
      
      // Also fetch payment plan calculation if no active plan
      if (!data.plan) {
        const calcResponse = await fetch(`${API_URL}/api/weekends/bookings/${bookingId}/payment-plan-calculation`, {
          headers: {
            'X-Booking-Token': token
          }
        });
        if (calcResponse.ok) {
          const calcData = await calcResponse.json();
          setPaymentPlanCalc(calcData);
        }
      }
    } catch (error) {
      console.error('Error fetching payment plan:', error);
    }
  };

  const fetchStoredCards = async (customerEmail, token = bookingToken) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/weekends/customer/${encodeURIComponent(customerEmail)}/stored-cards`, {
        headers: {
          'X-Booking-Token': token
        }
      });
      const data = await response.json();
      setStoredCards(data.cards || []);
    } catch (error) {
      console.error('Error fetching stored cards:', error);
    }
  };

  const handleStoreCard = async (paymentToken, verificationToken) => {
    if (isTokenExpired()) {
      handleTokenExpired();
      return { success: false, error: 'Session expired' };
    }
    
    try {
      const response = await fetch(`${API_URL}/api/weekends/bookings/${bookingData.booking.booking_id}/store-card`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getBookingAuthHeaders()
        },
        body: JSON.stringify({
          source_id: paymentToken,
          cardholder_name: bookingData.booking.customer_name,
          verification_token: verificationToken
        })
      });
      
      if (response.status === 401) {
        handleTokenExpired();
        return { success: false, error: 'Session expired' };
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Show appropriate message based on whether it's a new card or existing
        if (data.message) {
          toast.success(data.message);
        } else {
          toast.success('Card saved successfully!');
        }
        setSelectedCardId(data.card_id);
        // Refresh stored cards
        fetchStoredCards(bookingData.booking.customer_email);
        setPaymentPlanStep(3); // Move to confirm step
        return { success: true };
      } else {
        toast.error(data.error || 'Failed to save card');
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Error storing card:', error);
      toast.error('Failed to save card');
      return { success: false, error: error.message };
    }
  };

  const handleCreatePaymentPlan = async () => {
    if (isTokenExpired()) {
      handleTokenExpired();
      return;
    }
    
    if (!selectedCardId) {
      toast.error('Please select or add a card');
      return;
    }
    if (!customerConsent) {
      toast.error('Please consent to recurring charges');
      return;
    }
    
    setSavingPlan(true);
    try {
      const response = await fetch(`${API_URL}/api/weekends/bookings/${bookingData.booking.booking_id}/payment-plan`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getBookingAuthHeaders()
        },
        body: JSON.stringify({
          card_id: selectedCardId,
          customer_consent: true
        })
      });
      
      if (response.status === 401) {
        handleTokenExpired();
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Payment plan created successfully!');
        setPaymentPlan(data.plan);
        setShowPaymentPlanDialog(false);
        resetPaymentPlanForm();
      } else {
        toast.error(data.detail || data.error || 'Failed to create payment plan');
      }
    } catch (error) {
      console.error('Error creating payment plan:', error);
      toast.error('Failed to create payment plan');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleCancelPaymentPlan = async () => {
    if (isTokenExpired()) {
      handleTokenExpired();
      return;
    }
    
    if (!window.confirm('Are you sure you want to cancel your payment plan? Your remaining balance will still be due.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/weekends/bookings/${bookingData.booking.booking_id}/payment-plan/cancel`, {
        method: 'POST',
        headers: {
          ...getBookingAuthHeaders()
        }
      });
      
      if (response.status === 401) {
        handleTokenExpired();
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Payment plan cancelled');
        setPaymentPlan(null);
      } else {
        toast.error(data.detail || 'Failed to cancel payment plan');
      }
    } catch (error) {
      console.error('Error cancelling payment plan:', error);
      toast.error('Failed to cancel payment plan');
    }
  };

  const resetPaymentPlanForm = () => {
    setPaymentPlanStep(1);
    setSelectedCardId(null);
    setCustomerConsent(false);
  };

  const handleUpdateCard = async (nonce) => {
    if (isTokenExpired()) {
      handleTokenExpired();
      return;
    }
    
    if (!nonce) {
      toast.error('Card validation failed');
      return;
    }
    
    setUpdatingCard(true);
    try {
      const response = await fetch(`${API_URL}/api/weekends/bookings/update-card`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getBookingAuthHeaders()
        },
        body: JSON.stringify({
          booking_id: bookingData.booking.booking_id,
          card_nonce: nonce
        })
      });

      if (response.status === 401) {
        handleTokenExpired();
        return;
      }

      const responseText = await response.text();
      let data;
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid response');
      }

      if (response.ok && data.success) {
        toast.success(data.message || 'Card updated successfully!');
        setShowUpdateCardDialog(false);
        
        // Update local payment plan state with new card info
        if (paymentPlan) {
          setPaymentPlan({
            ...paymentPlan,
            card_last4: data.card_last4,
            card_brand: data.card_brand,
            last_payment_error: null
          });
        }
        
        // Refresh booking data
        lookupBooking({ preventDefault: () => {} });
      } else {
        toast.error(data.detail || 'Failed to update card');
      }
    } catch (error) {
      console.error('Error updating card:', error);
      toast.error('Failed to update card');
    } finally {
      setUpdatingCard(false);
    }
  };

  const handleAddAddon = async (addon) => {
    if (isTokenExpired()) {
      handleTokenExpired();
      return;
    }
    
    setAddonAdding(true);
    try {
      const response = await fetch(`${API_URL}/api/weekends/bookings/${bookingData.booking.booking_id}/add-addon`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...getBookingAuthHeaders()
        },
        body: JSON.stringify({
          addon_name: addon.name
        })
      });

      if (response.status === 401) {
        handleTokenExpired();
        return;
      }

      // Safari-safe: Read as text first, then parse JSON
      const responseText = await response.text();
      let data;
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid response');
      }

      if (response.ok && data.success) {
        toast.success(data.message);
        setSelectedAddon({
          ...addon,
          totalPrice: data.addon.total_price,
          totalDeposit: data.addon.total_deposit,
          newBalance: data.new_balance_due
        });
        setShowAddAddonDialog(false);
        setShowPaymentDialog(true);
        
        // Refresh booking data
        await lookupBooking({ preventDefault: () => {} });
      } else {
        toast.error(data.detail || 'Failed to add addon');
      }
    } catch (error) {
      console.error('Add addon error:', error);
      toast.error('Failed to add addon. Please try again.');
    } finally {
      setAddonAdding(false);
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    toast.success('Payment successful!');
    setShowPaymentDialog(false);
    setSelectedAddon(null);
    // Refresh booking data
    await lookupBooking({ preventDefault: () => {} });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending Payment', className: 'bg-amber-100 text-amber-800' },
      deposit_paid: { label: 'Deposit Paid', className: 'bg-blue-100 text-blue-800' },
      fully_paid: { label: 'Fully Paid', className: 'bg-emerald-100 text-emerald-800' },
      review_required: { label: 'Under Review', className: 'bg-amber-100 text-amber-800' },
      cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-stone-100 text-stone-700' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const booking = bookingData?.booking;
  const event = bookingData?.event;
  const availableAddons = bookingData?.available_add_ons || [];

  return (
    <div className="min-h-screen bg-[var(--bg-base)] py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2">Manage Your Booking</h1>
          <p className="text-stone-500">Look up your booking to view details or add extras</p>
        </motion.div>

        {/* Lookup Form */}
        <Card className="bg-white border-stone-200 mb-8">
          <CardContent className="pt-6">
            <form onSubmit={lookupBooking} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-stone-500 mb-2">Booking Reference</label>
                  <Input
                    placeholder="e.g., wknd_bk_abc123"
                    value={bookingRef}
                    onChange={(e) => setBookingRef(e.target.value)}
                    className="bg-stone-100 border-stone-200 text-stone-900"
                    data-testid="booking-ref-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-stone-500 mb-2">Email Address</label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-stone-100 border-stone-200 text-stone-900"
                    data-testid="booking-email-input"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-violet-600 hover:bg-violet-700"
                data-testid="lookup-booking-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Looking up...</>
                ) : (
                  <><Search className="w-4 h-4 mr-2" /> Find My Booking</>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Booking Details */}
        {booking && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Event & Status */}
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-xl text-stone-900">{booking.event_title}</CardTitle>
                    <p className="text-stone-500 text-sm mt-1">Ref: {booking.booking_id}</p>
                  </div>
                  {getStatusBadge(booking.payment_review_required ? 'review_required' : booking.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-stone-600">
                      <Calendar className="w-4 h-4 text-violet-600" />
                      <span>{formatDate(event?.start_date)} - {formatDate(event?.end_date)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-stone-600">
                      <Users className="w-4 h-4 text-violet-600" />
                      <span>{booking.total_guests} Guest{booking.total_guests !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-stone-600">
                      <Mail className="w-4 h-4 text-violet-600" />
                      <span>{booking.customer_email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-stone-600">
                      <Phone className="w-4 h-4 text-violet-600" />
                      <span>{booking.customer_phone || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rooms & Guests */}
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <CardTitle className="text-lg text-stone-900">Rooms & Guests</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Rooms */}
                  <div>
                    <h4 className="text-sm font-medium text-stone-500 mb-2">Rooms Booked</h4>
                    <div className="flex flex-wrap gap-2">
                      {booking.rooms?.map((room, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-stone-100 text-stone-600">
                          {room.quantity}× {room.room_type} ({room.guests} guests)
                        </Badge>
                      ))}
                    </div>
                  </div>
                  
                  {/* Guests */}
                  <div>
                    <h4 className="text-sm font-medium text-stone-500 mb-2">Guest Names</h4>
                    <div className="grid md:grid-cols-2 gap-2">
                      {booking.guests?.map((guest, idx) => (
                        <div key={idx} className="text-stone-600 text-sm">
                          {guest.first_name} {guest.last_name}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Add-ons */}
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-stone-900">Add-ons</CardTitle>
                  {availableAddons.length > 0 && (
                    <Button 
                      size="sm" 
                      onClick={() => setShowAddAddonDialog(true)}
                      className="bg-violet-600 hover:bg-violet-700"
                      data-testid="add-addon-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Extra
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {booking.selected_add_ons?.length > 0 ? (
                  <div className="space-y-2">
                    {booking.selected_add_ons.map((addon, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-emerald-600" />
                          <span className="text-stone-600">{addon.name}</span>
                        </div>
                        <span className="text-stone-500">£{addon.total_price?.toFixed(2) || addon.price?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-stone-500 text-sm">No add-ons selected</p>
                )}
              </CardContent>
            </Card>

            {/* Payment Summary */}
            <Card className="bg-white border-stone-200">
              <CardHeader>
                <CardTitle className="text-lg text-stone-900">Payment Summary</CardTitle>
              </CardHeader>
              <CardContent>
                {booking.finance_under_review ? (
                  <div data-testid="finance-under-review-banner" className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-800 whitespace-pre-line">{booking.finance_review_message}</p>
                    </div>
                  </div>
                ) : (
                <div className="space-y-3">
                  <div className="flex justify-between text-stone-600">
                    <span>Total Amount</span>
                    <span className="font-semibold">£{booking.total_amount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>Amount Paid</span>
                    <span className="text-emerald-600">£{(booking.payment_review_required ? booking.reconciled_amount_paid : booking.amount_paid)?.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-stone-200 pt-3 flex justify-between text-stone-900">
                    <span className="font-semibold">Balance Due</span>
                    <span className="font-bold text-lg">
                      £{(booking.payment_review_required
                          ? (booking.reconciled_balance_due ?? 0)
                          : Math.max(0, (booking.total_amount || 0) - (booking.amount_paid || 0))).toFixed(2)}
                    </span>
                  </div>
                </div>
                )}

                {booking.payment_review_required && (
                  <div data-testid="payment-review-banner" className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="text-amber-800 font-medium">Payment under review</p>
                        <p className="text-amber-700 mt-1">
                          Our records for this booking are being reconciled. The balance shown above reflects the payments we have confirmed. Please contact us if you have any questions before making a further payment.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {booking.total_amount > booking.amount_paid && (
                  <div className="mt-6 p-4 bg-violet-50 border border-violet-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="text-violet-700 font-medium">Payment Options</p>
                        <a 
                          href="https://square.link/u/OFRElNoP" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="inline-block mt-3 px-6 py-3 bg-black text-white font-semibold rounded-md hover:bg-gray-800 transition-colors"
                        >
                          Make A Card Payment
                        </a>
                        <p className="text-stone-500 mt-4">
                          <strong>Bank Transfer:</strong><br />
                          Bank: Virgin Money<br />
                          Name: Line Dance Central CIC<br />
                          Sort Code: 821208<br />
                          Account: 50242568
                        </p>
                        
                        {/* Payment Plan Option */}
                        {!paymentPlan && paymentPlanCalc?.available && (
                          <div className="mt-4 pt-4 border-t border-stone-200">
                            <p className="text-violet-700 font-medium mb-2">Or set up a Payment Plan</p>
                            <div className="bg-stone-50 p-3 rounded-lg mb-3">
                              <p className="text-stone-900 font-medium">
                                £{paymentPlanCalc.monthly_amount?.toFixed(2)}/month for {paymentPlanCalc.number_of_payments} months
                              </p>
                              <p className="text-stone-500 text-xs mt-1">
                                Final payment by {new Date(paymentPlanCalc.final_payment_date).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'long', year: 'numeric'
                                })}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                resetPaymentPlanForm();
                                setShowPaymentPlanDialog(true);
                              }}
                              className="border-violet-500 text-violet-600 hover:bg-violet-100"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Set Up Payment Plan
                            </Button>
                          </div>
                        )}
                        {!paymentPlan && paymentPlanCalc && !paymentPlanCalc.available && (
                          <div className="mt-4 pt-4 border-t border-stone-200">
                            <p className="text-stone-500 text-xs">
                              Payment plans: {paymentPlanCalc.reason}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Active Payment Plan Display */}
                {!booking.finance_under_review && paymentPlan && (
                  <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-emerald-600 font-medium flex items-center gap-2">
                          <RefreshCw className="w-4 h-4" />
                          Active Payment Plan
                        </p>
                        <div className="mt-2 space-y-1 text-sm">
                          <p className="text-stone-600">
                            <strong>Monthly Payment:</strong> £{paymentPlan.monthly_amount?.toFixed(2)}
                          </p>
                          <p className="text-stone-500">
                            <strong>Card:</strong> {paymentPlan.card_brand} ending {paymentPlan.card_last4}
                          </p>
                          <p className="text-stone-500">
                            <strong>Next Payment:</strong> {paymentPlan.next_payment_date 
                              ? new Date(paymentPlan.next_payment_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                              : 'N/A'}
                          </p>
                          <p className="text-stone-500">
                            <strong>Remaining Balance:</strong> £{paymentPlan.remaining_balance?.toFixed(2)}
                          </p>
                          <p className="text-stone-500">
                            <strong>Payments Made:</strong> {paymentPlan.payments_made}
                          </p>
                        </div>
                        
                        {/* Show payment error if exists */}
                        {paymentPlan.last_payment_error && (
                          <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-sm flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" />
                              {paymentPlan.last_payment_error}
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowUpdateCardDialog(true)}
                          className="text-blue-600 hover:text-blue-700 border-blue-200 hover:bg-blue-100"
                        >
                          <CreditCard className="w-3 h-3 mr-1" />
                          Update Card
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelPaymentPlan}
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                        >
                          Cancel Plan
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment History */}
            {booking.payment_records?.length > 0 && (
              <Card className="bg-white border-stone-200">
                <CardHeader>
                  <CardTitle className="text-lg text-stone-900">Payment History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {booking.payment_records.map((payment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <div>
                          <span className="text-stone-600">{payment.payment_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                          <span className="text-stone-500 text-sm ml-2">({payment.method})</span>
                          <p className="text-stone-500 text-xs">{formatDate(payment.date)}</p>
                        </div>
                        <span className="text-emerald-600 font-medium">£{payment.amount?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Notice */}
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
              <p className="text-stone-500 text-sm">
                <strong className="text-stone-600">Need help?</strong> If you have any questions about your booking 
                or haven't received your confirmation email, please contact us at{' '}
                <a href="mailto:hi@linedancecentral.uk" className="text-violet-600 hover:text-violet-700 underline">
                  hi@linedancecentral.uk
                </a>
              </p>
              
              {/* Update Card button - show if booking has card on file or payment plan */}
              {(booking.card_on_file_id || booking.card_last4 || paymentPlan) && (
                <div className="mt-4 pt-4 border-t border-stone-200">
                  <p className="text-stone-600 text-sm mb-2">
                    <strong>Payment Card:</strong> {booking.card_brand || paymentPlan?.card_brand || 'Card'} ending in {booking.card_last4 || paymentPlan?.card_last4 || '****'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUpdateCardDialog(true)}
                    className="text-blue-600 hover:text-blue-700 border-blue-200 hover:bg-blue-100"
                  >
                    <CreditCard className="w-3 h-3 mr-1" />
                    Update Payment Card
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Add Add-on Dialog */}
        <Dialog open={showAddAddonDialog} onOpenChange={setShowAddAddonDialog}>
          <DialogContent className="bg-white border-stone-200 text-stone-900">
            <DialogHeader>
              <DialogTitle>Add Extra to Your Booking</DialogTitle>
              <DialogDescription className="text-stone-500">
                Select an add-on to add to your booking
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-4">
              {availableAddons.map((addon, idx) => (
                <div 
                  key={idx}
                  className="p-4 bg-stone-100 rounded-lg border border-stone-200 hover:border-violet-500 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-stone-900">{addon.name}</h4>
                      {addon.description && (
                        <p className="text-stone-500 text-sm mt-1">{addon.description}</p>
                      )}
                      <p className="text-stone-600 text-sm mt-2">
                        £{addon.price?.toFixed(2)} per person
                        {addon.deposit_amount > 0 && (
                          <span className="text-stone-500"> (£{addon.deposit_amount?.toFixed(2)} deposit)</span>
                        )}
                      </p>
                      <p className="text-violet-600 text-sm mt-1">
                        Total for {booking?.total_guests} guests: £{(addon.price * (booking?.total_guests || 1)).toFixed(2)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddAddon(addon)}
                      disabled={addonAdding}
                      className="bg-violet-600 hover:bg-violet-700"
                    >
                      {addonAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* Payment Dialog for Add-on */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-md">
            <DialogHeader>
              <DialogTitle>Pay for {selectedAddon?.name}</DialogTitle>
              <DialogDescription className="text-stone-500">
                Complete payment for your add-on
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4">
              <div className="bg-stone-100 p-4 rounded-lg mb-4">
                <div className="flex justify-between text-stone-600 mb-2">
                  <span>Add-on Total</span>
                  <span>£{selectedAddon?.totalPrice?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-stone-900 font-semibold">
                  <span>Deposit Required</span>
                  <span>£{selectedAddon?.totalDeposit?.toFixed(2)}</span>
                </div>
              </div>
              
              {selectedAddon?.totalDeposit > 0 && booking && (
                <SquarePaymentForm
                  bookingId={booking.booking_id}
                  amount={Math.round(selectedAddon.totalDeposit * 100)}
                  paymentType="addon_deposit"
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={(error) => toast.error(error)}
                  customPaymentEndpoint={`${API_URL}/api/weekends/bookings/${booking.booking_id}/pay-addon`}
                  customPaymentBody={{}}
                  customHeaders={getBookingAuthHeaders()}
                />
              )}
              
              <p className="text-stone-500 text-xs text-center mt-4">
                Or pay via bank transfer and notify us
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Payment Plan Setup Dialog */}
        <Dialog open={showPaymentPlanDialog} onOpenChange={(open) => {
          setShowPaymentPlanDialog(open);
          if (!open) resetPaymentPlanForm();
        }}>
          <DialogContent className="bg-white border-stone-200 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-stone-900">Set Up Payment Plan</DialogTitle>
              <DialogDescription className="text-stone-500">
                Pay your remaining balance in monthly installments
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Progress indicator */}
              <div className="flex items-center justify-center gap-2 mb-4">
                {[1, 2, 3].map((step) => (
                  <div
                    key={step}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                      ${paymentPlanStep >= step 
                        ? 'bg-violet-600 text-white' 
                        : 'bg-stone-200 text-stone-500'}`}
                  >
                    {step}
                  </div>
                ))}
              </div>

              {/* Step 1: Show Payment Plan Details */}
              {paymentPlanStep === 1 && paymentPlanCalc?.available && (
                <div className="space-y-4">
                  <div className="bg-stone-50 p-4 rounded-lg space-y-3">
                    <div className="flex justify-between">
                      <span className="text-stone-500">Remaining Balance</span>
                      <span className="text-stone-900 font-medium">£{paymentPlanCalc.remaining_balance?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Monthly Payment</span>
                      <span className="text-stone-900 font-bold text-lg">£{paymentPlanCalc.monthly_amount?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Number of Payments</span>
                      <span className="text-stone-900">{paymentPlanCalc.number_of_payments} months</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">First Payment</span>
                      <span className="text-stone-900">
                        {new Date(paymentPlanCalc.first_payment_date).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-stone-500">Final Payment By</span>
                      <span className="text-stone-900">
                        {new Date(paymentPlanCalc.final_payment_date).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'long', year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-stone-500 text-xs">
                    Payments are processed on the 1st of each month. Your card will be automatically charged.
                  </p>

                  <Button
                    onClick={() => setPaymentPlanStep(2)}
                    className="w-full bg-violet-600 hover:bg-violet-500"
                  >
                    Continue to Add Card
                  </Button>
                </div>
              )}

              {/* Step 2: Add/Select Card */}
              {paymentPlanStep === 2 && (
                <div className="space-y-4">
                  <p className="text-stone-600 text-sm">
                    Select a saved card or add a new one for automatic payments.
                  </p>

                  {/* Existing cards */}
                  {storedCards.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-stone-500 text-xs uppercase tracking-wide">Saved Cards</p>
                      {storedCards.map((card) => (
                        <div
                          key={card.card_id}
                          onClick={() => {
                            setSelectedCardId(card.card_id);
                            setPaymentPlanStep(3);
                          }}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors
                            ${selectedCardId === card.card_id 
                              ? 'border-violet-500 bg-violet-100' 
                              : 'border-stone-200 hover:border-stone-300'}`}
                        >
                          <div className="flex items-center gap-3">
                            <CreditCard className="w-5 h-5 text-stone-500" />
                            <div>
                              <p className="text-stone-900 text-sm">{card.card_brand} •••• {card.last_4}</p>
                              <p className="text-stone-500 text-xs">Expires {card.exp_month}/{card.exp_year}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new card */}
                  <div className="border-t border-stone-200 pt-4">
                    <p className="text-stone-500 text-xs uppercase tracking-wide mb-3">Add New Card</p>
                    <SquarePaymentForm
                      amount={100}
                      title=""
                      subtitle="Your card will be securely stored for monthly payments"
                      buttonText="Save Card"
                      customerName={bookingData?.booking?.customer_name}
                      customerEmail={bookingData?.booking?.customer_email}
                      onPaymentSuccess={async (result) => {
                        // This is a card storage, not a payment
                        if (result.payment_token) {
                          await handleStoreCard(result.payment_token, result.verification_token);
                        }
                      }}
                      onPaymentError={(error) => toast.error(error)}
                      storeCardMode={true}
                      customPaymentHandler={async (token, verificationToken) => {
                        return await handleStoreCard(token, verificationToken);
                      }}
                    />
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => setPaymentPlanStep(1)}
                    className="w-full border-stone-300"
                  >
                    Back
                  </Button>
                </div>
              )}

              {/* Step 3: Confirm */}
              {paymentPlanStep === 3 && paymentPlanCalc?.available && (
                <div className="space-y-4">
                  <div className="bg-stone-50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Monthly Payment</span>
                      <span className="text-stone-900 font-medium">£{paymentPlanCalc.monthly_amount?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Remaining Balance</span>
                      <span className="text-stone-900">£{paymentPlanCalc.remaining_balance?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Number of Payments</span>
                      <span className="text-stone-900">{paymentPlanCalc.number_of_payments} months</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">First Payment</span>
                      <span className="text-stone-900">
                        {new Date(paymentPlanCalc.first_payment_date).toLocaleDateString('en-GB', { 
                          day: 'numeric', month: 'long', year: 'numeric' 
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Final Payment By</span>
                      <span className="text-stone-900">
                        {new Date(paymentPlanCalc.final_payment_date).toLocaleDateString('en-GB', { 
                          day: 'numeric', month: 'long', year: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>

                  {/* Consent checkbox */}
                  <label className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customerConsent}
                      onChange={(e) => setCustomerConsent(e.target.checked)}
                      className="mt-1"
                    />
                    <span className="text-stone-600 text-sm">
                      I authorize Line Dance Central to automatically charge my card £{paymentPlanCalc.monthly_amount?.toFixed(2)} 
                      on the 1st of each month until {new Date(paymentPlanCalc.final_payment_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. 
                      I understand I can cancel this plan at any time.
                    </span>
                  </label>

                  <Button
                    onClick={handleCreatePaymentPlan}
                    disabled={!customerConsent || savingPlan}
                    className="w-full bg-violet-600 hover:bg-violet-500"
                  >
                    {savingPlan ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Setting Up...
                      </>
                    ) : (
                      'Confirm Payment Plan'
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setPaymentPlanStep(2)}
                    className="w-full border-stone-300"
                    disabled={savingPlan}
                  >
                    Back
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Update Card Dialog */}
        <Dialog open={showUpdateCardDialog} onOpenChange={setShowUpdateCardDialog}>
          <DialogContent className="bg-white border-stone-200 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-stone-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Update Payment Card
              </DialogTitle>
              <DialogDescription className="text-stone-500">
                Enter your new card details below. This card will be used for all future payment plan installments.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <SquarePaymentForm 
                storeCardMode={true}
                amount={0}
                title="New Card Details"
                subtitle="Your card will be securely stored for future payments"
                buttonText={updatingCard ? "Updating..." : "Update Card"}
                disabled={updatingCard}
                customPaymentHandler={async (nonce) => {
                  await handleUpdateCard(nonce);
                  return { success: true };
                }}
                onPaymentSuccess={() => {}}
                onPaymentError={(error) => toast.error(error || "Failed to update card")}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
